import { calculateLevelProgress, formatNumber, getNextThreshold } from '@kwenta/sdk/utils'
import { type FC, memo, useMemo } from 'react'
import styled from 'styled-components'

import StartIcon from 'assets/svg/app/yellow-star.svg'
import ProgressBar from 'components/ProgressBar'
import { Body } from 'components/Text'
import { FlexDivCol, FlexDivRow, FlexDivRowCentered } from 'components/layout/flex'
import { useAppSelector } from 'state/hooks'
import { selectLevelThresholds } from 'state/points/selectors'
import media from 'styles/media'

type Props = {
	points: number
	color?: 'preview' | 'primary'
	size?: 'small' | 'large'
}

const PointsProgress: FC<Props> = memo(({ points, color = 'primary', size = 'large' }) => {
	const levelThresholds = useAppSelector(selectLevelThresholds)
	const progress = calculateLevelProgress(points)
	const nextThreshold = useMemo(() => {
		// use frontend hardcoded level thresholds if backend level thresholds are not available
		if (levelThresholds.length === 0) {
			return getNextThreshold(points)
		}
		return getNextThreshold(points, levelThresholds)
	}, [levelThresholds, points])

	return (
		<Container rowGap="5px">
			<FlexDivRow>
				<FlexDivRowCentered columnGap="5px">
					<StartIcon width={14} height={14} />
					<StyledBody mono weight="regular" size={size} color={color}>
						{formatNumber(points, { maxDecimals: 0 })}
					</StyledBody>
				</FlexDivRowCentered>
				<FlexDivRowCentered columnGap="5px">
					<StartIcon width={14} height={14} />
					<StyledBody mono weight="regular" size={size} color={color}>
						{nextThreshold ? formatNumber(nextThreshold, { maxDecimals: 0 }) : '∞'}
					</StyledBody>
				</FlexDivRowCentered>
			</FlexDivRow>
			<ProgressBar ratio={progress} height={size === 'large' ? 8 : 6} />
		</Container>
	)
})

const Container = styled(FlexDivCol)`
	width: 100%;
`
const StyledBody = styled(Body)`
	${media.lessThan('sm')`
		font-size: 9px;
	`}
`

export default PointsProgress
